import { FC } from 'react';
import { useState, useMemo, useCallback } from 'react';
import { getErrorMessage } from '@/utils/error';
import { FileText, Calendar, FileCheck } from 'lucide-react';
import { TabList, PageWrapper } from '@/components/ui';
import {
  SuggestedContentTab,
  PublishingCalendarTab,
  ContentModal,
  LinkedInConnectionModal,
} from '@/features/marketing';
import { useToast } from '@/contexts/ToastContext';
import { DocumentListTab } from '@/features/marketing/components/DocumentListTab';

import {
  useMarketingUserContext,
  useLinkedInIntegration,
  useSuggestedContent,
  usePublishingCalendar,
  useDocumentList,
} from '@/features/marketing/hooks';

const Marketing: FC = () => {
  const [activeTab, setActiveTab] = useState<string>("suggested");
  const { addToast } = useToast();

  // Get user context
  const {
    user,
    orgIdLoading,
    orgIdError,
    orgId,
    isInternalUserViewingExternal,
    effectiveOrgId,
    effectiveUserId,
    hasValidContext,
  } = useMarketingUserContext();

  // LinkedIn integration
  const {
    hasLinkedInToken,
    showLinkedInModal,
    isLinkedInModalClosable,
    openLinkedInModal,
    closeLinkedInModal,
    handleLinkedInConnect,
  } = useLinkedInIntegration();


  // Suggested Content Tab - enabled when active OR when calendar needs it
  // Calendar uses updatePost from suggested content when USE_POSTS_FOR_CALENDAR is true
  const suggestedContent = useSuggestedContent({
    effectiveOrgId,
    effectiveUserId,
    enabled: (activeTab === 'suggested' || activeTab === 'calendar') && hasValidContext,
    fetchData: activeTab === 'suggested', // Only fetch posts when suggested tab is active
  });

  // Publishing Calendar Tab - only enabled when active
  const publishingCalendar = usePublishingCalendar({
    effectiveOrgId,
    effectiveUserId,
    enabled: activeTab === 'calendar' && hasValidContext,
  });

  // Document List Tab - only enabled when active
  const documentList = useDocumentList({
    enabled: activeTab === 'list' && hasValidContext,
    userId: effectiveUserId,
  });

  // Lead Magnet Tab removed


  // Handle calendar update - coordinates between calendar and posts
  const handleCalendarUpdate = useCallback(async (
    id: number,
    updateData: { plannedDate?: string; dayNumber?: number; status?: string }
  ) => {
    if (publishingCalendar.USE_POSTS_FOR_CALENDAR) {
      // When using posts, update the post's plannedDate
      if (updateData.plannedDate && suggestedContent.updatePost) {
        try {
          await suggestedContent.updatePost(id, { plannedDate: updateData.plannedDate });
          await publishingCalendar.refetchCalendar();
          addToast('Post date updated successfully', 'success');
        } catch (error: unknown) {
          addToast(getErrorMessage(error), 'error');
          throw error;
        }
      }
    } else {
      // When using calendar table, use existing updateCalendarEntry
      return publishingCalendar.updateCalendarEntry(id, updateData);
    }
  }, [publishingCalendar, suggestedContent.updatePost, addToast]);

  // Handle document posting to LinkedIn
  const handleDocumentPostToLinkedIn = useCallback(async (documentId: string) => {
    await documentList.handleDocumentPostToLinkedIn(
      documentId,
      openLinkedInModal,
      hasLinkedInToken
    );
  }, [documentList, openLinkedInModal, hasLinkedInToken]);

  // Build tabs
  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: 'suggested',
        label: 'Suggested Content',
        icon: <FileText className="w-4 h-4" />,
      },
      {
        id: 'calendar',
        label: 'Publishing Calendar',
        icon: <Calendar className="w-4 h-4" />,
      },
    ];

    // Add tabs for external users OR internal users viewing an external user
    if (user?.role === 'external' || isInternalUserViewingExternal) {
      baseTabs.push(
        /*
          {
            id: 'testing',
            label: 'Social Testing',
            icon: <Wrench className="w-4 h-4" />,
          },
        */
        {
          id: 'list',
          label: 'Document List',
          icon: <FileCheck className="w-4 h-4" />,
        }
      );
    }

    return baseTabs;
  }, [user?.role, isInternalUserViewingExternal]);

  // Show loading state (only for external users or when no selectedUser)
  if (!isInternalUserViewingExternal && orgIdLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Show error state (only for external users or when no selectedUser)
  if (!isInternalUserViewingExternal && (orgIdError || !orgId)) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-red-600">
          {orgIdError || 'No company found. Please complete your profile setup.'}
        </div>
      </div>
    );
  }

  // Show message for internal users when no user is selected
  if (user?.role === 'internal' && !isInternalUserViewingExternal) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 md:p-8 flex items-center justify-center">
        <div className="text-gray-600">Please select a user to view their marketing data.</div>
      </div>
    );
  }

  return (
    <PageWrapper
      title="Marketing Performance"
      subtitle="Monitor your social footprint, manage publishing schedules, and review AI-generated content."
      tabs={
        <TabList
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
        />
      }
    >

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === "suggested" && (
          <SuggestedContentTab
            data={suggestedContent.suggestedItems}
            loading={suggestedContent.postsLoading}
            error={suggestedContent.postsError}
            onViewItem={suggestedContent.handleViewItem}
            onRemove={suggestedContent.handleRemove}
            onUpdate={suggestedContent.handleUpdatePost}
            onRefetch={suggestedContent.handleRefetchPosts}
            onRefetchCalendar={publishingCalendar.refetchCalendar}
            pagination={suggestedContent.pagination}
            onPaginationChange={suggestedContent.handlePaginationChange}
            onGlobalSearch={suggestedContent.handleGlobalSearch}
            globalFilterValue={suggestedContent.searchQuery}
            statusFilter={suggestedContent.statusFilter}
            onStatusFilterChange={suggestedContent.handleStatusFilterChange}
            sorting={suggestedContent.sorting}
            onSortingChange={suggestedContent.handleSortingChange}
            onEdit={suggestedContent.handleEditItem}
            onCloseEdit={suggestedContent.handleCloseEditModal}
            onSaveEdit={suggestedContent.handleSaveEdit}
            editingItem={suggestedContent.editingItem}
            isEditModalOpen={suggestedContent.isEditModalOpen}
          />
        )}

        {activeTab === "calendar" && (
          <PublishingCalendarTab
            calendar={publishingCalendar.calendarData}
            loading={publishingCalendar.calendarLoading}
            updatingEntryId={publishingCalendar.calendarUpdatingEntryId}
            error={publishingCalendar.calendarError}
            currentMonth={publishingCalendar.currentMonth}
            onMonthChange={publishingCalendar.handleMonthChange}
            onUpdateCalendarEntry={handleCalendarUpdate}
            onRefetchCalendar={publishingCalendar.refetchCalendar}
          />
        )}

        {/* {activeTab === "testing" && user?.role === 'external' && (
          <SocialPostingTest />
        )} */}

        {activeTab === "list" && (user?.role === 'external' || isInternalUserViewingExternal) && (
          <DocumentListTab
            data={documentList.documents}
            loading={documentList.documentsLoading}
            error={documentList.documentsError}
            pagination={documentList.pagination}
            onPaginationChange={documentList.handlePaginationChange}
            onGlobalSearch={documentList.handleDocumentSearch}
            globalFilterValue={documentList.docSearch}
            hasLinkedInToken={hasLinkedInToken}
            onDocumentUploaded={documentList.onDocumentUploaded}
            onPostToLinkedIn={handleDocumentPostToLinkedIn}
            onDocumentDeleted={documentList.deleteDocument}
          />
        )}
      </div>

      {/* Content Modal */}
      <ContentModal item={suggestedContent.modalItem} onClose={suggestedContent.handleCloseModal} />

      {/* LinkedIn Connection Modal */}
      {
        user?.role === 'external' && (
          <LinkedInConnectionModal
            isOpen={showLinkedInModal}
            // isOpen={false}
            onClose={closeLinkedInModal}
            onConnect={handleLinkedInConnect}
            closable={isLinkedInModalClosable}
          />
        )
      }
    </PageWrapper >
  );
};

export default Marketing;
